/**
 * Product/SKU system: design costs, quality→cost curves, review dynamics,
 * and price-competitiveness scoring.
 */

import { ARCHETYPES, SIZE_MIX_ASP_FACTOR } from './constants';
import { clamp } from './format';
import type { ArchetypeDef, ArchetypeId, GameState, Product } from './types';

export function archetype(id: ArchetypeId): ArchetypeDef {
  const a = ARCHETYPES.find((x) => x.id === id);
  if (!a) throw new Error(`Unknown archetype: ${id}`);
  return a;
}

/**
 * Factory (ex-works, China-reference) cost for a target quality.
 * Linear between the archetype's min/max quality cost anchors — documented
 * and intentionally simple so players can reason about spec trade-offs.
 */
export function factoryCostForQuality(a: ArchetypeDef, quality: number): number {
  const t = clamp((quality - 40) / 55, 0, 1);
  return Math.round(a.minQualityCost + t * (a.maxQualityCost - a.minQualityCost));
}

/** Blended realized ASP across the size mix (queen reference × mix factor). */
export function blendedAsp(p: Product): number {
  return p.price * SIZE_MIX_ASP_FACTOR;
}

export interface DesignSpec {
  name: string;
  archetype: ArchetypeId;
  quality: number;
  price: number;
}

/** Creates a product in `development` status; engine flips it to active later. */
export function createProduct(s: GameState, spec: DesignSpec): Product {
  const a = archetype(spec.archetype);
  const quality = clamp(Math.round(spec.quality), 30, 95);
  return {
    id: `sku_${s.time.day}_${Math.floor(Math.random() * 1e6).toString(36)}`,
    name: sanitizeName(spec.name),
    archetype: spec.archetype,
    status: 'development',
    devCompleteDay: s.time.day + a.devDays,
    price: clamp(Math.round(spec.price), 149, 3999),
    quality,
    baseFactoryCost: factoryCostForQuality(a, quality),
    landedCost: 0,
    coolingScore: Math.round(clamp(a.coolingBase + (quality - 60) * 0.4, 10, 98)),
    returnRateBase: a.returnRateBase,
    reviews: { rating: 4.2, count: 0 },
    unitsSoldTotal: 0,
    returnsTotal: 0,
  };
}

/** Product names are player input → strip anything exotic. */
export function sanitizeName(raw: string): string {
  return raw
    .replace(/[<>&"'`\\{}[\]$;]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 42) || 'Untitled Mattress';
}

// ---------------------------------------------------------------------------
// Scoring primitives shared by demand models
// ---------------------------------------------------------------------------

/** Price competitiveness vs archetype fair value, adjusted by market pressure. */
export function priceScore(s: GameState, p: Product): number {
  const fair = archetype(p.archetype).fairValue;
  const pressure = s.world.competition; // >1 = bloodbath pricing
  const effectiveFair = fair / (0.85 + 0.15 * pressure);
  const ratio = p.price / effectiveFair;
  // exp decay: at fair value ⇒ ~1.0; +20% over ⇒ ~0.67; -20% under ⇒ ~1.49
  return clamp(Math.exp(-2.2 * (ratio - 1)), 0.18, 1.7);
}

/** Review-driven trust score 0..~1.6 (log count curve like real marketplaces). */
export function reviewScore(p: Product): number {
  const ratingTerm = clamp((p.reviews.rating - 2.8) / 2.2, 0, 1); // 2.8★=0 … 5★=1
  const countTerm = Math.log10(1 + p.reviews.count) / 2.4; // saturates ~250 reviews
  return clamp(0.35 + ratingTerm * 0.75 + Math.min(countTerm, 1) * 0.45, 0.15, 1.65);
}

/** Quality-implied steady-state rating (returns drag it down). */
export function targetRating(s: GameState, p: Product): number {
  const base = 3.35 + (p.quality / 100) * 1.25 + (p.coolingScore / 100) * 0.25;
  const serviceBonus =
    (hasServicePlaybook(s) ? 0.12 : 0) +
    (s.team.hires.find((h) => h.id === 'cs-agent')?.hired ? 0.08 : 0);
  return clamp(base + serviceBonus, 1.9, 4.9);
}

function hasServicePlaybook(s: GameState): boolean {
  return s.research.completed.includes('service-playbook');
}
